import type React from 'react'
import {useState, useEffect} from 'react'
import {AutoCompleteWrapper} from '@/components/GDS/molecules/AutoCompleteWrapper'
import sharedStyle from '@/components/SelfOnboarding/Shared/OnboardingFormShared.module.css'
import {LINK_DASHBOARD_MODAL} from '@/components/SelfOnboarding/util/constants'

import {getProductDashboardsSearchByName} from '@/services/program/programService'
import {Label} from 'pepsico-ds'

import {useDispatch, useSelector} from 'react-redux'
import type {RootState} from '@/store'
import {setOldProgramReference} from '@/store/programOnboardingSlice'

interface LinkDashboardModalProps {
  onClose: (result?: any) => void
  selectedId: any
  dashboardType: 'team' | 'program'
  dashboardLabel?: string
  productId?: string
  onProductIdChange: (id: string, label: string, isSameProduct: boolean) => void
  openNewDashboardModal: () => Promise<{id: string} | undefined>
  openNewProgramModal?: () => Promise<{id: string} | undefined>
}

const LinkDashboardModal: React.FC<LinkDashboardModalProps> = ({onProductIdChange, ...props}) => {
  // Fetch from Redux
  const onboardingDashboard = useSelector((state: RootState) => state.onboardingDashboard)
  const oldProgramReference = useSelector((state: RootState) => state.oldProgramReference)
  const selectedDashboards =
    onboardingDashboard.productDashboardId && onboardingDashboard.programDashboardName
      ? [{id: onboardingDashboard.productDashboardId, label: onboardingDashboard.programDashboardName}]
      : []
  const dispatch = useDispatch()

  const isDark = useSelector((state: RootState) => state.theme.mode === 'dark')

  type Suggestion = {id: string; label: string; fullObject?: any}

  const [programDashboards, setProgramDashboards] = useState<Suggestion[]>(
    selectedDashboards.length > 0 ? selectedDashboards.map(d => ({...d, fullObject: {id: d.id, name: d.label}})) : [],
  )
  const [searching, setSearching] = useState(false)

  // On mount: fetch dashboards and auto-select if only one
  useEffect(() => {
    async function fetchDashboardsOnLoad(programName: string) {
      setSearching(true)
      try {
        const results = await getProductDashboardsSearchByName(programName)
        const mapped = Array.isArray(results)
          ? results.map((item: any) => ({
              id: item.id || item._id || item.value || item.label,
              label: item.name || item.label,
              fullObject: item,
            }))
          : []
        setProgramDashboards(mapped)
        const matchingProduct = mapped.find((item: any) => item.label === programName)

        if (matchingProduct) {
          onProductIdChange(matchingProduct.id, matchingProduct.label, true)
          dispatch(setOldProgramReference({oldProductId: matchingProduct.id, linkAction: 'TRANSFER'}))
        }
      } catch (err) {
        setProgramDashboards([])
      } finally {
        setSearching(false)
      }
    }
    const programName = oldProgramReference.oldConfigurationItemName
    if (programName) {
      fetchDashboardsOnLoad(programName)
    }
  }, [])

  // BuildWidget pattern: direct async search in onUpdate
  const handleSearch = async (query: string) => {
    if (!query || query.length <= 2) {
      setProgramDashboards([])
      return
    }
    setSearching(true)
    try {
      const results = await getProductDashboardsSearchByName(query)
      const mapped = Array.isArray(results)
        ? results.map((item: any) => ({
            id: item.id || item._id || item.value || item.label,
            label: item.name || item.label || item.value || item.id,
            fullObject: item,
          }))
        : []
      setProgramDashboards(mapped)
    } catch (err) {
      setProgramDashboards([])
    } finally {
      setSearching(false)
    }
  }

  return (
    <div className={sharedStyle.selfOnboardingWrapper}>
      <div className="flex w-[282px] flex-col items-start">
        <Label label={LINK_DASHBOARD_MODAL.label} />
        <AutoCompleteWrapper
          limitSuggestions={30}
          placeholderText={LINK_DASHBOARD_MODAL.placeholder}
          suggestions={programDashboards}
          isAutoComplete={true}
          searchTerm={selectedDashboards && selectedDashboards.length > 0 ? selectedDashboards[0].label : ''}
          onUpdate={handleSearch}
          onClear={() => {
            dispatch(
              setOldProgramReference({oldProductId: onboardingDashboard.productDashboardId, linkAction: 'REMOVE'}),
            )
            onProductIdChange('', '', false)
          }}
          onSelectOption={option => {
            const opt = option as Suggestion
            const isUpdate = !!oldProgramReference.oldConfigurationItemName
            dispatch(
              setOldProgramReference({
                oldProductId: isUpdate
                  ? onboardingDashboard.productDashboardId || oldProgramReference.oldProductId
                  : undefined,
                linkAction: isUpdate ? 'TRANSFER' : 'ADD',
              }),
            )
            onProductIdChange(opt?.fullObject?.id || '', opt?.label || '', false)
          }}
        />
        {searching && <div style={{color: '#888', marginTop: 4}}>{LINK_DASHBOARD_MODAL.loading}</div>}
      </div>
    </div>
  )
}

export default LinkDashboardModal
